import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import SocialShare from '../components/SocialShare';
import KitSubscriptionForm from '../components/KitSubscriptionForm';
import { getBlogPostBySlug, getRecentBlogPosts } from '../data/blogPosts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiCalendar, FiUser, FiArrowRight } = FiIcons;

/**
 * Extract FAQs from blog post content
 * Looks for patterns like <h4>Question</h4><p>Answer</p> within faq-section divs
 */
function extractFaqsFromContent(content) {
  if (!content) return [];

  const faqs = [];
  // Match FAQ sections: <h4>Question</h4> followed by <p>Answer</p>
  const faqRegex = /<h4[^>]*>([^<]+)<\/h4>\s*<p[^>]*>([\s\S]*?)<\/p>/gi;
  let match;

  while ((match = faqRegex.exec(content)) !== null) {
    const question = match[1].trim();
    // Strip HTML tags from answer
    const answer = match[2].replace(/<[^>]*>/g, '').trim();
    if (question && answer) {
      faqs.push({ q: question, a: answer });
    }
  }

  return faqs;
}

/**
 * Individual Blog Post Page
 * Displays blog post content with BlogPosting schema for SEO
 */
export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);
  const recentPosts = getRecentBlogPosts(3).filter(p => p.slug !== slug);

  // Extract FAQs from content or use dedicated faqs field
  // Must be called before any conditional returns (React hooks rules)
  const faqs = useMemo(() => {
    if (!post) return [];
    if (post.faqs && Array.isArray(post.faqs) && post.faqs.length > 0) {
      return post.faqs;
    }
    return extractFaqsFromContent(post.content);
  }, [post]);

  // Estimate word count from HTML content
  const wordCount = useMemo(() => {
    if (!post?.content) return 0;
    return post.content.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
  }, [post]);

  // Reading progress bar
  const [readProgress, setReadProgress] = useState(0);
  const handleScroll = useCallback(() => {
    const el = document.getElementById('article-body');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const total = el.scrollHeight;
    const visible = window.innerHeight;
    const scrolled = Math.max(0, -rect.top);
    setReadProgress(Math.min(100, Math.round((scrolled / (total - visible)) * 100)));
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const postUrl = `https://www.greengasturbines.com/blog/${post.slug}`;
  const imageUrl = `https://www.greengasturbines.com${post.featuredImage.url}`;

  // Generate BlogPosting schema
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    alternativeHeadline: post.seoTitle,
    description: post.metaDescription,
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      width: post.featuredImage.width,
      height: post.featuredImage.height
    },
    author: {
      '@type': 'Organization',
      name: post.author,
      url: 'https://www.greengasturbines.com'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Green Gas Turbines',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.greengasturbines.com/logo.png'
      }
    },
    datePublished: post.publishDate,
    dateModified: post.modifiedDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl
    },
    keywords: post.tags.join(', '),
    articleSection: post.category,
    inLanguage: 'en-US',
    wordCount: wordCount,
    timeRequired: post.readTime ? `PT${parseInt(post.readTime)}M` : undefined
  };

  // Generate FAQPage schema if FAQs exist
  const faqSchema = faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a
      }
    }))
  } : null;

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Blog', path: '/blog' },
    { label: post.title, path: null }
  ];

  return (
    <>
      <Helmet>
        <title>{post.seoTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={postUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content={String(post.featuredImage.width)} />
        <meta property="og:image:height" content={String(post.featuredImage.height)} />
        <meta property="og:image:alt" content={post.featuredImage.alt} />
        <meta property="og:site_name" content="Green Gas Turbines" />
        <meta property="og:locale" content="en_US" />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:modified_time" content={post.modifiedDate} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@GreenGasTs" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:image:alt" content={post.featuredImage.alt} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Reading Progress Bar */}
        <div
          className="fixed top-0 left-0 h-1 bg-primary-500 z-[60] transition-all duration-150"
          style={{ width: `${readProgress}%` }}
          role="progressbar"
          aria-valuenow={readProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Reading progress"
        />
        <Header />

        {/* Article Header */}
        <article>
          <section className="bg-gradient-to-br from-secondary-900 to-secondary-800 text-white py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="mb-6">
                  <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {post.category}
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {post.title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-gray-200">
                  <div className="flex items-center">
                    <SafeIcon icon={FiUser} className="h-5 w-5 mr-2" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="h-5 w-5 mr-2" />
                    <time dateTime={post.publishDate}>
                      {new Date(post.publishDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </time>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiClock} className="h-5 w-5 mr-2" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Featured Image */}
          <section className="bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="rounded-xl overflow-hidden shadow-2xl"
              >
                <img
                  src={post.featuredImage.url}
                  alt={post.featuredImage.alt}
                  className="w-full h-96 object-cover"
                />
              </motion.div>
            </div>
          </section>

          {/* Article Content */}
          <section className="py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <Breadcrumbs items={breadcrumbItems} />

              {/* Social Share - Top */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <SocialShare
                  url={postUrl}
                  title={post.title}
                  description={post.excerpt}
                />
              </div>

              {/* Article Body */}
              <div
                id="article-body"
                className="prose prose-lg max-w-none
                  prose-headings:text-secondary-900 prose-headings:font-bold
                  prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-6 prose-h2:leading-tight
                  prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:leading-snug
                  prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:mt-0
                  prose-strong:text-secondary-900 prose-strong:font-semibold
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                  prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2
                  prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-2
                  prose-li:mb-2 prose-li:text-gray-700 prose-li:leading-relaxed
                  prose-blockquote:border-l-4 prose-blockquote:border-primary-500 
                  prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-600
                  prose-table:w-full prose-table:my-10 prose-table:border-collapse
                  prose-thead:bg-gray-100 prose-thead:border-b-2 prose-thead:border-gray-300
                  prose-th:bg-gray-100 prose-th:text-left prose-th:font-semibold prose-th:py-4 prose-th:px-4 prose-th:border-r prose-th:border-gray-200 prose-th:last:border-r-0
                  prose-tbody:divide-y prose-tbody:divide-gray-200
                  prose-tr:border-b prose-tr:border-gray-200
                  prose-td:py-4 prose-td:px-4 prose-td:border-r prose-td:border-gray-200 prose-td:last:border-r-0
                  [&_table]:!border [&_table]:!border-gray-300 [&_table]:!rounded-lg [&_table]:!overflow-hidden
                  [&_tbody_tr:nth-child(even)]:bg-gray-50
                  [&_tbody_tr:hover]:bg-gray-100 [&_tbody_tr]:transition-colors
                  [&_pre]:text-secondary-900 [&_pre]:bg-gray-50
                  [&_pre]:border [&_pre]:border-gray-200 [&_pre]:rounded-lg
                  [&_pre]:px-4 [&_pre]:py-3 [&_pre]:text-sm [&_pre]:leading-relaxed
                  [&_pre]:overflow-x-auto [&_pre]:my-6"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Tags:
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Share - Bottom */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <SocialShare
                  url={postUrl}
                  title={post.title}
                  description={post.excerpt}
                />
              </div>

              {/* Newsletter Subscription */}
              <div className="mt-12">
                <KitSubscriptionForm
                  title="Stay Updated on Clean Energy"
                  description="Get the latest insights on hydrogen turbines, energy transition strategies, and industry analysis delivered to your inbox."
                />
              </div>

              {/* CTA Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-12 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 text-center"
              >
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Ready to Decarbonize Your Power Generation?
                </h3>
                <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                  Contact our team for a customized feasibility study and ROI analysis
                  tailored to your facility's needs.
                </p>
                <Link
                  to="/#quote"
                  className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  Request Consultation
                </Link>
              </motion.div>
            </div>
          </section>
        </article>

        {/* Related Posts */}
        {recentPosts.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-secondary-900 mb-8">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {recentPosts.map((relatedPost) => (
                  <motion.article
                    key={relatedPost.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <Link to={`/blog/${relatedPost.slug}`}>
                      <img
                        src={relatedPost.featuredImage.url}
                        alt={relatedPost.featuredImage.alt}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                    </Link>
                    <div className="p-6">
                      <Link to={`/blog/${relatedPost.slug}`}>
                        <h3 className="text-xl font-bold text-secondary-900 mb-2 hover:text-primary-600 transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                      </Link>
                      <p className="text-gray-600 mb-4 line-clamp-2 text-sm">
                        {relatedPost.excerpt}
                      </p>
                      <Link
                        to={`/blog/${relatedPost.slug}`}
                        className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm"
                      >
                        Read More
                        <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}
